import React from "react";
import { Flex, HelpIndicator, Tag } from "@jsq/ds/core";
import { investorProgramRenderer, mutedTag } from "./styles_vanilla.css";

export interface InvestorProgramRendererProps {
  /** Investor compliance program name (Main `ComplianceSettingsModel.compliance_program`). */
  complianceProgram?: string | null;
  /** Whether the investor program is enabled; when false the program badge is shown muted. */
  complianceEnabled: boolean;
  /** FATCA/CRS data collection toggle — renders an additive "FATCA/CRS" badge. */
  fatcaCrsEnabled: boolean;
  /** Legacy KYC (Main `is_investor_diligence_enabled`) — display-only investor-dimension badge. */
  isInvestorDiligenceEnabled: boolean;
}

const EMPTY = "--";

/**
 * Investor-program cell for the Entities tab. Composition (FE-owned; BE exposes the four raw
 * signals): a primary badge — the compliance program (muted when not enabled), else "Legacy KYC"
 * when investor diligence is on, else "--" — plus an additive FATCA/CRS badge when enabled.
 */
export const InvestorProgramRenderer: React.FC<InvestorProgramRendererProps> = ({
  complianceProgram,
  complianceEnabled,
  fatcaCrsEnabled,
  isInvestorDiligenceEnabled,
}) => {
  // Unique per rendered cell so tooltip ids don't collide across grid rows.
  const idBase = React.useId();
  const primaryTag = complianceProgram ? (
    <HelpIndicator
      id={`${idBase}-program`}
      placement="top"
      content={`${complianceProgram} program is ${complianceEnabled ? "enabled" : "disabled"}`}
    >
      <Tag
        data-testid="investor-program-tag"
        label={complianceProgram}
        variant="neutral"
        className={complianceEnabled ? undefined : mutedTag}
      />
    </HelpIndicator>
  ) : isInvestorDiligenceEnabled ? (
    <HelpIndicator
      id={`${idBase}-legacy`}
      placement="top"
      content="Legacy KYC program is enabled"
    >
      <Tag data-testid="legacy-kyc-tag" label="Legacy KYC" variant="neutral" />
    </HelpIndicator>
  ) : (
    <HelpIndicator id={`${idBase}-empty`} placement="top" content="No program assigned yet">
      <span>{EMPTY}</span>
    </HelpIndicator>
  );

  return (
    <Flex className={investorProgramRenderer} gap={2} alignItems="center">
      {primaryTag}
      {fatcaCrsEnabled && (
        <HelpIndicator
          id={`${idBase}-fatca`}
          placement="top"
          content="FATCA/CRS program is enabled"
        >
          <Tag data-testid="fatca-crs-tag" label="FATCA/CRS" variant="neutral" />
        </HelpIndicator>
      )}
    </Flex>
  );
};

export default InvestorProgramRenderer;
